import { Avatar } from './Avatar';
import type { BaseCabItemOption } from './index.types';
import { Summary } from './Summary';

export interface CabCardProps extends BaseCabItemOption {}

const CapacityMap: Record<keyof CabCardProps['capacity'], 'PAX' | 'BAGGAGE'> = {
	travellers: 'PAX',
	luggage: 'BAGGAGE',
};

/**
 * Item component displays a complete cab option with icon, details, and capacity information
 *
 * @param iconUrl - URL of the cab icon image
 * @param vehicleType - Type of vehicle (e.g., "SUV", "Hatchback")
 * @param capacity - Passenger and luggage capacity information
 * @param model - Vehicle model description
 * @returns JSX element with complete cab option display
 *
 *
 * Possible errors:
 * - Invalid capacity data will result in empty capacity display
 * - Image loading errors handled by Avatar component
 *
 * Exception handling:
 * - Gracefully handles missing or invalid note data
 * - CapacityMap transformation ensures proper capacity type mapping
 * - Child components handle their respective error cases
 */
export const Item = ({
	imageUrl,
	vehicleType,
	cabPartner,
	model,
	capacity,
	fuelType,
}: CabCardProps) => {
	// Transform capacity data with error handling
	const capacityData = Object.entries(capacity)
		.map(([key, value]) => {
			const capacityType = CapacityMap[key as keyof CabCardProps['capacity']];
			return {
				type: capacityType,
				title: value,
			};
		})
		.filter((item) => item.type); // Remove items with invalid types

	return (
		// Decoration layer for the cab item
		<div className="px-15 py-5 flex items-center gap-x-10 bg-tertiary">
			<Avatar
				iconUrl={imageUrl || ''}
				vehicleType={vehicleType}
				fuelType={fuelType}
			/>
			<Summary
				vehicleType={vehicleType}
				model={model}
				capacityData={capacityData}
				cabPartner={cabPartner}
			/>
		</div>
	);
};
